// Radha — Credit / Debit notes (Zoho Books style). Issue a credit note (reduces
// what a customer owes) or a debit note (raises it), optionally against an
// invoice; the note number is generated server-side. Lists the tenant's notes
// with a net effect on receivables, and exports to Excel.
import React, { useMemo, useState } from "react";

import { useInvoices } from "../hooks/useBatchModules";
import { useCreditNotes, useCreateCreditNote } from "../hooks/useCreditNotes";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import { ExportButton } from "../components/TableSearch";
import { useRole } from "../lib/authSupabase";
import { fmtMoney as inr, homeCurrency } from "../lib/money";


const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 8px" };
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const fld: React.CSSProperties = { padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" };

function CreateNote({ customers, invoicesByCustomer }: { customers: string[]; invoicesByCustomer: Map<string, string[]> }) {
  const create = useCreateCreditNote();
  const [type, setType] = useState<"credit" | "debit">("credit");
  const [customer, setCustomer] = useState("");
  const [invoice, setInvoice] = useState("");
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [tax, setTax] = useState("");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const invoices = customer ? invoicesByCustomer.get(customer) ?? [] : [];
  const canSave = customer && Number(amount) > 0;

  const submit = () => {
    setNote(null);
    create.mutate(
      { note_type: type, customer_name: customer, invoice_number: invoice || null, reason: reason || null, amount: Number(amount), tax_amount: Number(tax) || 0 },
      {
        onSuccess: (r) => { setNote({ ok: true, text: `Issued ${r.note_number}` }); setAmount(""); setTax(""); setReason(""); setInvoice(""); },
        onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
      },
    );
  };

  return (
    <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 10, padding: "12px 14px", marginBottom: 22, background: "var(--surface-2)" }}>
      <div style={{ font: "600 13px Inter", marginBottom: 8 }}>Issue a note</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={type} onChange={(e) => setType(e.target.value as "credit" | "debit")} style={fld}>
          <option value="credit">Credit note (−)</option>
          <option value="debit">Debit note (+)</option>
        </select>
        <select value={customer} onChange={(e) => { setCustomer(e.target.value); setInvoice(""); }} style={{ ...fld, minWidth: 180 }}>
          <option value="">Customer…</option>
          {customers.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={invoice} onChange={(e) => setInvoice(e.target.value)} style={fld} disabled={!customer}>
          <option value="">Invoice (optional)…</option>
          {invoices.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder={`Amount (${homeCurrency()})`} style={{ ...fld, width: 120 }} />
        <input value={tax} onChange={(e) => setTax(e.target.value)} type="number" placeholder="of which GST" style={{ ...fld, width: 120 }} />
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" style={{ ...fld, minWidth: 160, flex: 1 }} />
        <button onClick={submit} disabled={!canSave || create.isPending}
          style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: canSave ? "pointer" : "default", fontSize: 13, opacity: canSave ? 1 : 0.5 }}>
          {create.isPending ? "Issuing…" : "Issue note"}
        </button>
      </div>
      {note && <span style={{ display: "inline-block", marginTop: 8, fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
    </div>
  );
}

export function CreditNotesPanel() {
  const { data: notes, isLoading, error } = useCreditNotes();
  const { data: invoices } = useInvoices();
  const { canWrite } = useRole();

  const { customers, invoicesByCustomer } = useMemo(() => {
    const byCust = new Map<string, string[]>();
    for (const i of invoices ?? []) {
      const arr = byCust.get(i.customer_name) ?? [];
      arr.push(i.invoice_number); byCust.set(i.customer_name, arr);
    }
    return { customers: [...byCust.keys()].sort(), invoicesByCustomer: byCust };
  }, [invoices]);

  const totals = useMemo(() => {
    const list = notes ?? [];
    const credit = list.filter((n) => n.note_type === "credit").reduce((s, n) => s + Number(n.amount), 0);
    const debit = list.filter((n) => n.note_type === "debit").reduce((s, n) => s + Number(n.amount), 0);
    return { credit, debit, net: debit - credit };
  }, [notes]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 28, margin: "4px 0 18px", flexWrap: "wrap" }}>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Credit notes</div><div style={{ font: "600 20px Inter", color: "var(--status-good)" }}>−{inr(totals.credit)}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Debit notes</div><div style={{ font: "600 20px Inter", color: "var(--status-critical)" }}>+{inr(totals.debit)}</div></div>
        <div title="Debit notes minus credit notes — net effect on receivables.">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Net effect</div>
          <div style={{ font: "600 20px Inter" }}>{totals.net < 0 ? "−" : "+"}{inr(Math.abs(totals.net))}</div>
        </div>
      </div>

      {canWrite && <CreateNote customers={customers} invoicesByCustomer={invoicesByCustomer} />}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <ExportButton name="Credit-Debit Notes" rows={() => (notes ?? []).map((n) => ({
          Number: n.note_number, Type: n.note_type, Customer: n.customer_name, Invoice: n.invoice_number ?? "",
          Amount: Number(n.amount), GST: Number(n.tax_amount), Reason: n.reason ?? "",
          Issued: n.issued_at.slice(0, 10), Status: n.status,
        }))} />
      </div>

      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
              <th style={th}>Number</th><th style={th}>Type</th><th style={th}>Customer</th>
              <th style={th}>Invoice</th><th style={{ ...th, textAlign: "right" }}>Amount</th><th style={th}>Reason</th><th style={th}>Issued</th>
            </tr>
          </thead>
          <tbody>
            {(notes ?? []).map((n) => (
              <tr key={n.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{n.note_number}</td>
                <td style={{ ...td, color: n.note_type === "credit" ? "var(--status-good)" : "var(--status-critical)", textTransform: "capitalize" }}>{n.note_type}</td>
                <td style={td}>{n.customer_name}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{n.invoice_number ?? "—"}</td>
                <td style={numTd}>{n.note_type === "credit" ? "−" : "+"}{inr(n.amount)}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{n.reason ?? "—"}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{n.issued_at.slice(0, 10)}</td>
              </tr>
            ))}
            {(notes ?? []).length === 0 && (
              <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={7}>No credit or debit notes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
